/// <reference types="@sveltejs/kit" />
import { dev } from "$app/environment";
import { goto } from "$app/navigation";
import type { AppSyncAttachment } from "$appsync/system/AppSyncAttachmentList";
import { AppSyncType } from "$appsync/system/AppSyncType";
import type { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { BasicEvent } from "@h4x/common";
import { addToast, type Type as Variants } from "@hewmen-internal/heui";
import type { ButtonProps } from "@hewmen-internal/heui/components/Button/Button.svelte";
import { getContext } from "svelte";
import { get, writable, type Readable, type Subscriber, type Unsubscriber, type Writable } from "svelte/store";
import ErrorReport from "./components/ErrorReport.svelte";

export class ObservableSet<T> {
	public onUpdate = new BasicEvent<() => void>();

	private set = new Set<T>();

	get items() {
		return this.set;
	}

	public add(value: T) {
		console.info("Adding value", value, this);
		this.set.add(value);
		this.onUpdate.execute();
		return this;
	}

	public delete(value: T) {
		console.info("Deleting value", value, this);
		let ret = this.set.delete(value);
		this.onUpdate.execute();
		return ret;
	}

	public clear() {
		this.set.clear();
		this.onUpdate.execute();
	}

	public subscribe(run: Subscriber<Set<T>>): Unsubscriber {
		let ref = this.onUpdate.addCallback(() => {
			run(this.set);
		});

		run(this.set);

		return () => {
			ref.remove();
		};
	}

	public reorder(value: T) {
		console.info("Reordering Set:", value, this);
		this.delete(value);
		this.set = new Set([value, ...this.set]);
		return this;
	}
}

export type TabbedPageContext = {
	actionButtons?: (ButtonProps & { label?: string } & ({ dispatchModalEvent?: string } | { onClick: () => void }))[];
	filter?: Record<string, unknown>[];
	itemCount?: Writable<number>;
	loadable?: boolean;
	loading?: boolean;
	searchString?: string;
	selections?: ObservableSet<any>;
	supportsItemCount?: boolean;
	supportsSearch?: boolean;
	supportsViewToggle?: boolean;
	viewType?: Writable<"card" | "list">;
};

const DEFAULT_CONTEXT: TabbedPageContext = {
	actionButtons: [],
	itemCount: writable(0),
	loadable: true,
	loading: false,
	searchString: "",
	selections: new ObservableSet<any>(),
	supportsItemCount: true,
	supportsSearch: true,
	supportsViewToggle: true
};

type StripWritable<Type> = Type extends Writable<infer U> ? StripWritable<U> : Type;

export type UpdateTabbedPageContext = {
	[Key in keyof TabbedPageContext]: StripWritable<TabbedPageContext[Key]>;
};

type StripSetPrefix<S extends string> = S extends `set${infer P1}${infer P2}` ? `${Lowercase<P1>}${P2}` : S;
export type UpdateTabbedPageContextFunctions = {
	[Key in `set${Capitalize<keyof TabbedPageContext>}`]: (
		value: Exclude<StripWritable<TabbedPageContext[StripSetPrefix<Key>]>, null | undefined>
	) => void;
};

export type TabbedPageFullContext = TabbedPageContext & UpdateTabbedPageContextFunctions;

export const getPageContext = <
	FullType extends boolean = false,
	Return = FullType extends true ? TabbedPageFullContext : TabbedPageContext
>(): Return => {
	return getContext<Return>("TabbedPage");
};

export type AppSyncDataStore = {
	initialLoad: () => Promise<boolean>;
	fetchAll: () => Promise<void>;
	fetchMore: () => Promise<boolean>;
	items: any[];
	loading: boolean;
	updating: boolean;
};

export const updateTabbedPageContext = (values?: UpdateTabbedPageContext, dataStore?: Readable<AppSyncDataStore>) => {
	if (!values) {
		return;
	}

	let context = getContext<TabbedPageFullContext>("TabbedPage");
	values = { ...DEFAULT_CONTEXT, ...values } as UpdateTabbedPageContext;

	if (dataStore) {
		const storeValue = get(dataStore);
		values = { ...values, ...{ itemCount: storeValue.items.length, loading: storeValue.loading } };
	}

	// eslint-disable-next-line guard-for-in
	for (const key in values) {
		const functionName = `set${key.slice(0, 1).toUpperCase() + key.slice(1)}` as keyof UpdateTabbedPageContextFunctions;
		if (typeof context?.[functionName] !== "function") {
			console.error(`[TabbedPageContext.updateTabbedPageContext] Update method \"${functionName}\" not found`);
			return;
		}

		context[functionName](
			// @ts-expect-error The typings on this are complex, not sure how to coalesce them to the proper type
			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
			values[key]
		);
	}
};

export const useLocalStorage = <Type>(key: string, defaultValue?: Type): Writable<Type> => {
	if (typeof window === "undefined") {
		return writable(defaultValue);
	}

	const value = writable(defaultValue);
	let localValue = localStorage.getItem(key);
	if (localValue) {
		value.set(JSON.parse(localValue) as Type);
	}

	value.subscribe((latestValue) => {
		if (localValue === latestValue) {
			return;
		}

		localStorage.setItem(key, JSON.stringify(latestValue));
	});

	window.addEventListener("storage", () => {
		const storedValue = localStorage.getItem(key);
		if (storedValue == null) {
			return;
		}

		localValue = JSON.parse(storedValue) as string;
		if (localValue !== get(value)) {
			value.set(localValue as Type);
		}
	});

	return value;
};

const DEBUG_LOCAL_STORAGE_KEY = "HEWMEN_DEBUG";

export const inDebugMode = () => {
	return get(useLocalStorage(DEBUG_LOCAL_STORAGE_KEY, "false")) !== "false";
};

export const disableDebugMode = () => {
	localStorage.removeItem(DEBUG_LOCAL_STORAGE_KEY);
	window.location.reload();
};

export const enableDebugMode = () => {
	localStorage.setItem(DEBUG_LOCAL_STORAGE_KEY, "true");
	window.location.reload();
};

export const DEFAULT_VARIANTS: Record<Variants, string[]> = {
	default: ["bg-app-wrapper-nav-item-background"],
	error: ["bg-message-error-background"],
	fourth: ["bg-app-wrapper-background"],
	info: ["bg-message-info-background"],
	plain: ["bg-transparent"],
	primary: [],
	secondary: ["bg-card-secondary-background"],
	success: ["bg-message-success-background"],
	warn: ["bg-message-warn-background"]
};

const CAPITALIZED_DATA_KEYS = ["id", "pk", "sk"];

export const mapKeyToLabel = (key: string) => {
	if (key === "createdAt") {
		return "Creation Date";
	} else if (CAPITALIZED_DATA_KEYS.includes(key)) {
		return key.toUpperCase();
	} else {
		let value = key.replace(/[A-Z]/g, (txt) => {
			return ` ${txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()}`;
		});
		return value.slice(0, 1).toUpperCase() + value.slice(1);
	}
};

export const navigateTo = (currentPage?: null | string, to?: null | string) => {
	if (dev) {
		console.info(`[${currentPage}] Navigating to ${to}...`);
	}

	goto(to).catch((error) => {
		console.error(`[${currentPage}] Unable to navigate to ${to}:`, error);
	});
};

export type ViewType = "card" | "list";

export type SelectionRecord = { id: string; name: string };
export type SelectionRecordFull = SelectionRecord & { value: boolean };

export type Route = {
	children?: Record<string, Route>;
	exact?: boolean;
	icon?: IconDefinition;
	image?: string;
	label: string;
	showInNav?: boolean;
	kind?: string;
	videoIcon?: string;
	visual?: boolean;
};

export const handleSelection = (selections: ObservableSet<any>, { detail }: CustomEvent<SelectionRecordFull>) => {
	if (selections.items.size > 25) {
		addToast({ message: "More than 25 items cannot be selected", type: "warn" });
		return;
	}

	let item = detail.item as AppSyncType | AppSyncAttachment<AppSyncType, AppSyncType>;

	if (detail.value) {
		console.info("Added value");
		if (!selections.items.has(item)) {
			selections.add(item);
			item.onRemove.addCallback(selections.delete, selections);
		}
	} else {
		console.info("Removed value");
		if (selections.items.has(item)) {
			selections.delete(item);
			item.onRemove.removeCallback(selections.delete, selections);
		}
	}
};

const showError = (error: Error) => {
	let body = document.body;

	let div = document.createElement("div");
	div.id = "error-report";
	div.style.position = "fixed";
	div.style.top = "0";
	div.style.left = "0";
	div.style.width = "100%";
	div.style.height = "100%";
	div.style.backgroundColor = "rgba(0,0,0,0.5)";
	div.style.zIndex = "999999";
	div.style.display = "flex";
	div.style.alignItems = "center";
	div.style.justifyContent = "center";

	new ErrorReport({
		target: div,
		props: {
			error: error
		}
	});

	body.appendChild(div);
};

export const registerErrorHandlers = () => {
	window.addEventListener("error", (event) => {
		console.error("[onError]", event);
		showError(event.error);
	});

	window.addEventListener("unhandledrejection", (event) => {
		console.error("[onUnhandledRejection]", event);
		showError(event.reason);
	});
};

export const addDatasetProp = (node: HTMLElement, id: string) => {
	if (node?.dataset) {
		node.dataset.autohide = id;
		const children = node?.children;

		for (let i = 0; i < children.length; i++) {
			const el = children[i] as HTMLElement;
			el.dataset.autohide = id;
			addDatasetProp(el, id);
		}
	}

	return {
		destroy() {}
	};
};
